<template>
  <div>
    <site-header />
    <main>
      <section class="hero-section interactive-hero" :style="heroStyle" @mousemove="handleHeroMove" @mouseleave="resetHero">
        <img class="hero-image" :src="heroImage" alt="Menswear editorial">
        <div class="hero-scanline"></div>
        <div class="hero-content">
          <p>New Season Menswear</p>
          <h1>Street-ready layers, denim, and everyday essentials.</h1>
          <router-link to="/items" class="primary-link">Shop New Arrivals</router-link>
        </div>
        <div class="hero-scroll-cue">Scroll</div>
      </section>

      <section class="home-category-showcase">
        <router-link
          v-for="(tile, index) in categoryTiles"
          :key="tile.category"
          :to="{ path: '/items', query: { category: tile.category } }"
          class="category-tile"
          :class="{ active: activeCategoryIndex === index }"
          @mouseenter.native="activeCategoryIndex = index"
          @focus.native="activeCategoryIndex = index"
        >
          <img :src="tile.image" :alt="tile.category">
          <div>
            <span>{{ tile.kicker }}</span>
            <strong>{{ tile.category }}</strong>
          </div>
        </router-link>
      </section>

      <section class="category-preview">
        <div>
          <p>Now previewing</p>
          <h2>{{ activeCategoryTile.category }}</h2>
          <span>{{ activeCategoryTile.description }}</span>
        </div>
        <div class="tone-row">
          <i v-for="tone in activeCategoryTile.tones" :key="tone" :style="{ background: tone }"></i>
        </div>
        <router-link :to="{ path: '/items', query: { category: activeCategoryTile.category } }">Open category</router-link>
      </section>

      <section class="deal-band">
        <div>
          <p>Weekend Refresh</p>
          <h2>Buy more, style more</h2>
          <span>Layer hoodies, cargo bottoms, tees, and denim for a full rotation under budget.</span>
        </div>
        <router-link to="/items">Shop the edit</router-link>
      </section>

      <section class="section-block">
        <div class="section-title">
          <p>Fresh Picks</p>
          <h2>Built for right now</h2>
          <router-link to="/items">View all</router-link>
        </div>
        <div v-if="featuredLoading" class="loading-state">Loading products...</div>
        <div v-else-if="featuredProducts.length" class="product-grid">
          <product-card v-for="product in featuredProducts" :key="product.id" :product="product" />
        </div>
        <div v-else class="inline-empty">No products found.</div>
      </section>

      <section class="lookbook-section">
        <div class="lookbook-copy">
          <p>Style Notes</p>
          <h2>Three ways to wear the drop</h2>
          <span>Start with a graphic tee, add one statement layer, then finish with denim or cargos. Simple pieces, sharp proportions.</span>
        </div>
        <div class="lookbook-grid">
          <article v-for="look in looks" :key="look.title">
            <img :src="look.image" :alt="look.title">
            <div>
              <span>{{ look.label }}</span>
              <strong>{{ look.title }}</strong>
              <router-link :to="{ path: '/items', query: { category: look.category } }">Shop look</router-link>
            </div>
          </article>
        </div>
      </section>

      <section class="section-block compact-products">
        <div class="section-title">
          <p>Most Wanted</p>
          <h2>Trending now</h2>
          <router-link to="/items">Shop all</router-link>
        </div>
        <div v-if="trendingLoading" class="loading-state">Loading products...</div>
        <div v-else-if="trendingProducts.length" class="product-grid">
          <product-card v-for="product in trendingProducts" :key="product.id" :product="product" />
        </div>
        <div v-else class="inline-empty">No trending products found.</div>
      </section>

      <section class="editorial-grid">
        <router-link :to="{ path: '/items', query: { category: 'Denim + Jeans' } }" class="editorial-tile">
          <img :src="editorialImages.denim" alt="Denim collection">
          <span>Denim that works all week</span>
        </router-link>
        <router-link :to="{ path: '/items', query: { category: 'Activewear + Athletic' } }" class="editorial-tile">
          <img :src="editorialImages.activewear" alt="Activewear collection">
          <span>Performance pieces for off-duty days</span>
        </router-link>
      </section>

      <section class="service-strip">
        <div>
          <strong>Free shipping</strong>
          <span>Orders over $50</span>
        </div>
        <div>
          <strong>Easy returns</strong>
          <span>30-day return window</span>
        </div>
        <div>
          <strong>New drops</strong>
          <span>Fresh styles weekly</span>
        </div>
        <div>
          <strong>Secure checkout</strong>
          <span>Fast demo payment flow</span>
        </div>
      </section>
    </main>
    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import ProductCard from '@/components/ProductCard.vue'
import { categories } from '@/data/products'
import { queryGoods } from '@/api/goods'

export default {
  name: 'Home',
  components: { SiteHeader, SiteFooter, ProductCard },
  data() {
    return {
      categories,
      heroImage: require('@/assets/img/home/mens-hero.jpg'),
      editorialImages: {
        denim: require('@/assets/img/home/editorial-denim.jpg'),
        activewear: require('@/assets/img/home/editorial-activewear.jpg')
      },
      featuredProducts: [],
      trendingProducts: [],
      featuredLoading: false,
      trendingLoading: false,
      activeCategoryIndex: 0,
      heroX: 50,
      heroY: 50,
      revealObserver: null,
      categoryTiles: [
        {
          category: 'Sweatshirts + Hoodies',
          kicker: 'Soft layers',
          description: 'Relaxed fleece, clean hoodies, and easy layers for daily movement.',
          tones: ['#111111', '#6e7567', '#f0d85a'],
          image: require('@/assets/img/home/hoodies-category.jpg')
        },
        {
          category: 'Jackets + Coats',
          kicker: 'Outerwear edit',
          description: 'Structured coats and jackets that sharpen the whole fit instantly.',
          tones: ['#1d2626', '#8f877e', '#ffffff'],
          image: require('@/assets/img/home/jackets-category.jpg')
        },
        {
          category: 'Graphic Tees',
          kicker: 'Statement graphics',
          description: 'Print-led tees made to anchor denim, cargos, and weekend shorts.',
          tones: ['#111111', '#b7352d', '#f6f1e8'],
          image: require('@/assets/img/home/graphic-tees-category.jpg')
        },
        {
          category: 'Denim + Jeans',
          kicker: 'Washed denim',
          description: 'Blue washes, straight fits, and dependable denim for every rotation.',
          tones: ['#183a59', '#6f8ca8', '#d8d0c7'],
          image: require('@/assets/img/home/denim-category.jpg')
        }
      ],
      looks: [
        {
          label: 'Look 01',
          title: 'Campus hoodie with cargos',
          category: 'Sweatshirts + Hoodies',
          image: require('@/assets/img/home/look-hoodie-cargo.jpg')
        },
        {
          label: 'Look 02',
          title: 'Denim jacket over clean essentials',
          category: 'Essentials',
          image: require('@/assets/img/home/look-denim-essentials.jpg')
        },
        {
          label: 'Look 03',
          title: 'Training layers for weekends',
          category: 'Activewear + Athletic',
          image: require('@/assets/img/home/look-activewear.jpg')
        }
      ]
    }
  },
  computed: {
    heroStyle() {
      return {
        '--hero-x': `${this.heroX}%`,
        '--hero-y': `${this.heroY}%`,
        '--hero-offset-x': `${(50 - this.heroX) * 0.08}px`,
        '--hero-offset-y': `${(50 - this.heroY) * 0.08}px`
      }
    },
    activeCategoryTile() {
      return this.categoryTiles[this.activeCategoryIndex] || this.categoryTiles[0]
    }
  },
  created() {
    this.loadHomeProducts()
  },
  mounted() {
    this.initReveal()
  },
  beforeDestroy() {
    if (this.revealObserver) {
      this.revealObserver.disconnect()
    }
  },
  methods: {
    handleHeroMove(event) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.heroX = Math.round(((event.clientX - rect.left) / rect.width) * 100)
      this.heroY = Math.round(((event.clientY - rect.top) / rect.height) * 100)
    },
    resetHero() {
      this.heroX = 50
      this.heroY = 50
    },
    initReveal() {
      const items = this.$el.querySelectorAll('.home-category-showcase, .category-preview, .deal-band, .section-block, .lookbook-section, .editorial-grid, .service-strip')
      items.forEach((item) => item.classList.add('reveal-item'))

      if (!('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'))
        return
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.16 })

      items.forEach((item) => this.revealObserver.observe(item))
    },
    async loadHomeProducts() {
      this.featuredLoading = true
      this.trendingLoading = true
      try {
        const featured = await queryGoods({ num: 1, size: 8 })
        this.featuredProducts = featured.list
      } finally {
        this.featuredLoading = false
      }

      try {
        const trending = await queryGoods({ num: 2, size: 4 })
        this.trendingProducts = trending.list
      } finally {
        this.trendingLoading = false
      }
    }
  }
}
</script>
